import {
  activate,
  ensureInitialized,
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  onConfigUpdate,
  RemoteConfig,
  setCustomSignals,
} from 'firebase/remote-config';
import {
  DEFAULT_REMOTE_CONFIG,
  RemoteConfigKey,
  RemoteConfigValues,
} from '../config/firebase/remoteConfig';
import { app } from './firebase';
import { notifications } from '@mantine/notifications';

let remoteConfig: RemoteConfig | null = null;

let initializationPromise: Promise<void> | null = null;

let unsubscribeConfigUpdate: (() => void) | null = null;

type RemoteConfigValueType = string | number | boolean;

type ConfigUpdateCallback = () => void;
const configUpdateCallbacks: Set<ConfigUpdateCallback> = new Set();

const SERIALIZED_DEFAULT_CONFIG: Record<string, string | number | boolean> =
  Object.fromEntries(
    Object.entries(DEFAULT_REMOTE_CONFIG).map(([key, value]) => [
      key,
      Array.isArray(value) ? JSON.stringify(value) : value,
    ])
  );

const initializeRemoteConfigInstance = (): RemoteConfig | null => {
  if (!app) {
    console.warn('Firebase app not initialized. Remote Config unavailable.');
    return null;
  }

  if (remoteConfig) {
    return remoteConfig;
  }

  try {
    remoteConfig = getRemoteConfig(app);
    remoteConfig.defaultConfig = SERIALIZED_DEFAULT_CONFIG;

    // Set custom signals for targeting
    const environment = import.meta.env.VITE_APP_ENV || 'dev';
    setCustomSignals(remoteConfig, { environment });

    return remoteConfig;
  } catch (error) {
    console.error('Error initializing Remote Config:', error);
    return null;
  }
};

export const ensureRemoteConfigReady = async (): Promise<void> => {
  if (!initializationPromise) {
    initializationPromise = (async () => {
      const config = initializeRemoteConfigInstance();
      if (!config) return;

      await ensureInitialized(config);
      await fetchAndActivate(config);
    })();
  }

  return initializationPromise;
};

export const initializeRemoteConfig = (): RemoteConfig | null => {
  return initializeRemoteConfigInstance();
};

export const fetchRemoteConfig = async (): Promise<boolean> => {
  const config = initializeRemoteConfigInstance();
  if (!config) {
    return false;
  }

  try {
    await ensureRemoteConfigReady();

    const activated = await fetchAndActivate(config);

    if (activated) {
      console.info('Remote Config fetched and activated');
    } else {
      console.info('Remote Config fetched (no changes)');
    }

    return true;
  } catch (error) {
    console.error('Error fetching Remote Config:', error);
    return false;
  }
};

export function getConfig<K extends RemoteConfigKey>(
  key: K
): RemoteConfigValues[K];
export function getConfig<K extends RemoteConfigKey>(
  key: K,
  defaultValue: RemoteConfigValues[K]
): RemoteConfigValues[K];
export function getConfig<K extends RemoteConfigKey>(
  key: K,
  defaultValue?: RemoteConfigValues[K]
): RemoteConfigValueType {
  const defaultValueType = DEFAULT_REMOTE_CONFIG[key];
  const fallback = defaultValue ?? DEFAULT_REMOTE_CONFIG[key];

  // Check for global override to enable all features
  const enableAllFeatures = import.meta.env.VITE_ENABLE_ALL_FEATURES === 'true';

  if (
    enableAllFeatures &&
    typeof defaultValueType === 'boolean' &&
    key.toString().startsWith('enable_')
  ) {
    return true;
  }

  // Check for environment variable override for specific key
  // Keeping this as it's still useful for non-boolean values or specific overrides
  const overrideKey = `VITE_OVERRIDE_${key.toUpperCase()}`;
  const overrideValue = import.meta.env[overrideKey];

  if (overrideValue !== undefined) {
    if (typeof defaultValueType === 'boolean') {
      return overrideValue === 'true';
    }

    if (typeof defaultValueType === 'number') {
      return Number(overrideValue);
    }

    if (Array.isArray(defaultValueType)) {
      try {
        return JSON.parse(overrideValue) as RemoteConfigValues[K];
      } catch {
        console.warn(
          `Invalid JSON for env override "${overrideKey}", ignoring override`
        );
      }
    } else {
      return overrideValue as RemoteConfigValues[K];
    }
  }

  const config = initializeRemoteConfigInstance();

  if (!config) {
    return fallback;
  }

  try {
    const value = getValue(config, key);

    if (typeof defaultValueType === 'boolean') {
      return value.asBoolean();
    }

    if (typeof defaultValueType === 'number') {
      return value.asNumber();
    }

    if (Array.isArray(defaultValueType)) {
      try {
        return JSON.parse(value.asString()) as RemoteConfigValues[K];
      } catch {
        console.error(
          `Invalid JSON for Remote Config key "${key}", falling back to default`
        );
        return fallback;
      }
    }

    return value.asString() as RemoteConfigValues[K];
  } catch (error) {
    console.error(`Error getting Remote Config value for "${key}":`, error);
    return fallback;
  }
}

export const getConfigValue = getConfig;

let isListening = false;

export const subscribeToConfigUpdates = (
  callback: ConfigUpdateCallback
): (() => void) => {
  configUpdateCallbacks.add(callback);

  // Start the listener if this is the first subscriber and we aren't already listening
  if (configUpdateCallbacks.size === 1 && !isListening) {
    startConfigUpdateListener();
  }

  // Return unsubscribe function
  return () => {
    configUpdateCallbacks.delete(callback);
    // Stop listener if no subscribers left
    if (configUpdateCallbacks.size === 0 && isListening) {
      stopConfigUpdateListener();
    }
  };
};

const startConfigUpdateListener = async () => {
  if (unsubscribeConfigUpdate || isListening) {
    return;
  }

  const config = initializeRemoteConfigInstance();
  if (!config) {
    console.warn('Cannot start config update listener: config not initialized');
    return;
  }

  try {
    isListening = true;
    await ensureRemoteConfigReady();

    // Double check if we should still be listening after the async operation
    // (in case everyone unsubscribed while we were waiting)
    if (configUpdateCallbacks.size === 0) {
      isListening = false;
      return;
    }

    unsubscribeConfigUpdate = onConfigUpdate(config, {
      next: (configUpdate) => {
        if (configUpdate.getUpdatedKeys().has('api_base_url')) {
          notifications.show({
            title: 'Configuration Updated',
            message: 'API endpoint changed. Reloading...',
            color: 'orange',
            autoClose: false,
          });

          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return;
        }

        activate(config)
          .then((activated) => {
            if (activated) {
              console.log('Remote Config changes activated');
              // Notify all subscribers
              configUpdateCallbacks.forEach((callback) => callback());
            }
          })
          .catch((error) => {
            console.error('Error activating Remote Config update:', error);
          });
      },
      error: (error) => {
        console.error('Config update error:', error);
      },
      complete: () => {
        console.log('Config update listener completed');
      },
    });

    console.log('Remote Config update listener started');
  } catch (error) {
    console.error('Error starting config update listener:', error);
    isListening = false;
  }
};

const stopConfigUpdateListener = () => {
  if (unsubscribeConfigUpdate) {
    unsubscribeConfigUpdate();
    unsubscribeConfigUpdate = null;
  }
  isListening = false;
  console.log('Remote Config update listener stopped');
};
